import { apiClient } from "@/taskpane/utils/client";
import { Tokens } from "@/taskpane/types/auth";
import axios from "axios";

// dotenv-webpack loads all env variables without prefix requirement
// But we use REACT_APP_ prefix for consistency with React conventions
// Fallback values are provided from .env file defaults
const SSO_CLIENT_ID = process.env.SSO_CLIENT_PARTENAIRE_ID || 
                      process.env.REACT_APP_SSO_CLIENT_PARTENAIRE_ID ||
                      'clientext-partenaire-412f3619-1fc1-4529-9214-3a0dceee46ba';
const SSO_AUTHORIZE_URL = process.env.REACT_APP_SSO_AUTHORIZE_URL || 
                          'https://login.genapicloud.com/authorization/OAuth2/Authorize';
const SSO_REDIRECT_URI = process.env.REACT_APP_SSO_REDIRECT_URI || 
                         'https://prod-addin-api.bylaw.fr/auth/sso/callback';
const SSO_EXCHANGE_CODE_ENDPOINT = process.env.REACT_APP_SSO_EXCHANGE_CODE_ENDPOINT || 
                                   '/auth/sso/exchange-code';
const SSO_EXCHANGE_TOKEN_ENDPOINT = process.env.REACT_APP_SSO_EXCHANGE_ENDPOINT || 
                                    '/auth/sso/exchange-token';
const SSO_TOKEN_URL = process.env.REACT_APP_SSO_TOKEN_URL || 
                      'https://login.genapicloud.com/Authorization/OAuth/Token';
const SSO_STUDIES_URL = process.env.REACT_APP_SSO_STUDIES_ENDPOINT || 
                        'https://api-public.genapicloud.com/sso/v2/identite/etudes';

// Debug logging - log SSO configuration at module load time
console.log('[SSO Module] Configuration loaded:', {
  SSO_EXCHANGE_CODE_ENDPOINT,
  SSO_EXCHANGE_TOKEN_ENDPOINT,
  env_REACT_APP_SSO_EXCHANGE_CODE_ENDPOINT: process.env.REACT_APP_SSO_EXCHANGE_CODE_ENDPOINT,
  env_REACT_APP_SSO_EXCHANGE_ENDPOINT: process.env.REACT_APP_SSO_EXCHANGE_ENDPOINT,
});

export interface SSOCallbackParams {
  code: string;
  state: string;
}

export interface Office {
  Nom: string;
  NumeroCRPCEN: string;
  TenantId: string;
}

/**
 * Generate cryptographically secure random state for CSRF protection
 */
export function generateState(): string {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Initiate SSO login flow - redirects to GenAPI authorization endpoint
 * 
 * @param customState - Optional custom state for cross-context SSO (Desktop Outlook).
 *                      If provided, this state will be used instead of generating a new one.
 *                      This allows the polling session ID to match the OAuth state.
 */
export function initiateSSO(customState?: string): void {
  // Validate environment variables
  if (!SSO_CLIENT_ID) {
    console.error('[SSO] SSO_CLIENT_PARTENAIRE_ID is not defined in environment');
    throw new Error('SSO configuration error: SSO_CLIENT_PARTENAIRE_ID not configured');
  }
  if (!SSO_AUTHORIZE_URL) {
    console.error('[SSO] REACT_APP_SSO_AUTHORIZE_URL is not defined in environment');
    throw new Error('SSO configuration error: REACT_APP_SSO_AUTHORIZE_URL not configured');
  }
  if (!SSO_REDIRECT_URI) {
    console.error('[SSO] REACT_APP_SSO_REDIRECT_URI is not defined in environment');
    throw new Error('SSO configuration error: REACT_APP_SSO_REDIRECT_URI not configured');
  }

  console.log('[SSO] Environment variables loaded:', {
    clientId: SSO_CLIENT_ID,
    authorizeUrl: SSO_AUTHORIZE_URL,
    redirectUri: SSO_REDIRECT_URI,
  });

  // Use provided state (for Desktop Outlook) or generate new one (for web/mobile)
  const state = customState || generateState();
  
  // Store state for validation on callback
  // Use localStorage instead of sessionStorage because Outlook Add-ins may reload the taskpane
  localStorage.setItem('sso_state', state);
  localStorage.setItem('sso_initiated_at', Date.now().toString());
  console.log('[SSO] ==================== INITIATING SSO ====================');
  console.log('[SSO] Stored state in localStorage:', state.substring(0, 10) + '...', {
    isCustomState: !!customState,
    context: customState ? 'Desktop Outlook (cross-context)' : 'Web/Mobile (standard)',
    fullState: state,
    timestamp: new Date().toISOString()
  });
  console.log('[SSO] Full state value for debugging:', state);
  console.log('[SSO] ===========================================================');
  
  // Build authorization URL
  const params = new URLSearchParams({
    response_type: 'code',
    client_id: SSO_CLIENT_ID,
    redirect_uri: SSO_REDIRECT_URI,
    state: state,
  });
  
  const authUrl = `${SSO_AUTHORIZE_URL}?${params.toString()}`;
  
  console.log('[SSO] Initiating SSO login, redirecting to:', authUrl);
  console.log('[SSO] Redirect URI:', SSO_REDIRECT_URI);
  console.log('[SSO] State parameter in URL:', state);
  
  // Redirect to SSO provider
  window.location.href = authUrl;
}

/**
 * Exchange OAuth2 authorization code for GenAPI access token (via backend)
 * 
 * This calls the backend Lambda function which securely exchanges the code
 * for a GenAPI access token using the client_secret stored in AWS Secrets Manager.
 * 
 * Flow:
 * 1. Validate CSRF state parameter (with fallback for cross-context scenarios)
 * 2. Call backend /auth/sso/exchange-code with authorization code and redirect_uri
 * 3. Backend exchanges code for GenAPI token using client_secret
 * 4. Return GenAPI access token to frontend
 * 
 * Note: In Outlook Add-ins, the SSO flow opens in an external browser,
 * so localStorage may not be shared between contexts. We check both
 * localStorage and sessionStorage, and if no stored state is found,
 * we proceed anyway (the backend will validate the code).
 * 
 * @param code - OAuth2 authorization code from GenAPI
 * @param state - CSRF protection state parameter
 * @returns GenAPI access token
 */
export async function exchangeCodeForToken(code: string, state: string): Promise<string> {
  try {
    // console.log('═══════════════════════════════════════════════════════════════');
    // console.log('[SSO] 🔵 STEP 1: exchangeCodeForToken() called');
    // console.log('[SSO] Function: exchangeCodeForToken');
    // console.log('[SSO] Endpoint constant: SSO_EXCHANGE_CODE_ENDPOINT =', SSO_EXCHANGE_CODE_ENDPOINT);
    // console.log('[SSO] Endpoint value:', SSO_EXCHANGE_CODE_ENDPOINT);
    // console.log('[SSO] Code (first 20 chars):', code.substring(0, 20) + '...');
    // console.log('[SSO] State (first 20 chars):', state.substring(0, 20) + '...');
    // console.log('═══════════════════════════════════════════════════════════════');
    
    // Try to validate state for CSRF protection
    // Check both localStorage and sessionStorage for cross-context compatibility
    const storedStateLocal = localStorage.getItem('sso_state');
    const storedStateSession = sessionStorage.getItem('sso_state');
    const storedState = storedStateLocal || storedStateSession;
    
    // console.log('[SSO] State validation:', { 
    //   storedInLocalStorage: storedStateLocal, 
    //   storedInSessionStorage: storedStateSession,
    //   received: state 
    // });
    
    // Only validate if we have a stored state
    // In cross-context scenarios (Outlook -> Browser), the state may not be available
    if (storedState && storedState !== state) {
      console.error('[SSO] State validation failed', { stored: storedState, received: state });
      throw new Error('Invalid state parameter - possible CSRF attack');
    }
    
    // if (storedState) {
    //   console.log('[SSO] State validated successfully');
    // } else {
    //   console.warn('[SSO] No stored state found - this is expected in cross-context SSO (Outlook Add-in)');
    //   console.log('[SSO] Proceeding with code exchange - backend will validate the authorization code');
    // }
    
    // Clear stored state from both storages
    localStorage.removeItem('sso_state');
    localStorage.removeItem('sso_initiated_at');
    sessionStorage.removeItem('sso_state');
    sessionStorage.removeItem('sso_initiated_at');
    
    // Call backend endpoint to exchange code for token
    // The backend has client_secret stored securely in AWS Secrets Manager
    // IMPORTANT: We send redirect_uri so the backend uses the same redirect_uri
    // that we used for the initial authorization request (required by OAuth2 spec)
    // console.log('[SSO] 📤 Making POST request to:', SSO_EXCHANGE_CODE_ENDPOINT);
    // console.log('[SSO] Using redirect_uri:', SSO_REDIRECT_URI);
    // console.log('[SSO] Request body:', { code: code.substring(0, 20) + '...', redirect_uri: SSO_REDIRECT_URI });
    
    const response = await apiClient.post(SSO_EXCHANGE_CODE_ENDPOINT, {
      code,
      redirect_uri: SSO_REDIRECT_URI,
    });
    
    if (!response.data || !response.data.access_token) {
      console.error('[SSO] ❌ Invalid response from backend:', response.data);
      throw new Error('Backend did not return access_token');
    }
    
    // console.log('[SSO] ✅ Step 1 SUCCESS: Received GenAPI access token from backend');
    // console.log('[SSO] GenAPI token (first 50 chars):', response.data.access_token.substring(0, 50) + '...');
    // console.log('═══════════════════════════════════════════════════════════════');
    return response.data.access_token;
    
  } catch (error) {
    console.error('═══════════════════════════════════════════════════════════════');
    console.error('[SSO] ❌❌❌ ERROR in exchangeCodeForToken()');
    console.error('[SSO] Error object:', error);
    if (axios.isAxiosError(error)) {
      console.error('[SSO] Axios error details:');
      console.error('  - URL:', error.config?.url);
      console.error('  - Method:', error.config?.method);
      console.error('  - Status:', error.response?.status);
      console.error('  - Response data:', error.response?.data);
      console.error('  - Request data:', error.config?.data);
    }
    console.error('═══════════════════════════════════════════════════════════════');
    throw new Error('Failed to exchange authorization code for GenAPI token');
  }
}

/**
 * Exchange GenAPI access token for Cognito tokens via backend
 * 
 * This calls the backend Lambda function which:
 * 1. Validates the GenAPI token
 * 2. Uses the selected CRPCEN (if provided) or retrieves from GenAPI token
 * 3. Creates or authenticates user in Cognito with the selected office
 * 4. Returns Cognito tokens for the frontend
 * 
 * @param genapiToken - GenAPI access token
 * @param selectedCrpcen - Optional CRPCEN selected by user (for multi-office users)
 * @param selectedTenantId - Optional TenantId selected by user (for multi-office users)
 * @returns Cognito authentication tokens
 */
export async function exchangeSSOToken(
  genapiToken: string, 
  selectedCrpcen?: string, 
  selectedTenantId?: string
): Promise<Tokens> {
  try {
    // console.log('═══════════════════════════════════════════════════════════════');
    // console.log('[SSO] 🟢 STEP 3: exchangeSSOToken() called');
    // console.log('[SSO] Function: exchangeSSOToken');
    // console.log('[SSO] Endpoint constant: SSO_EXCHANGE_TOKEN_ENDPOINT =', SSO_EXCHANGE_TOKEN_ENDPOINT);
    // console.log('[SSO] Endpoint value:', SSO_EXCHANGE_TOKEN_ENDPOINT);
    // console.log('[SSO] Environment variable REACT_APP_SSO_EXCHANGE_ENDPOINT:', process.env.REACT_APP_SSO_EXCHANGE_ENDPOINT);
    // console.log('[SSO] GenAPI token (first 50 chars):', genapiToken.substring(0, 50) + '...');
    // console.log('[SSO] Selected CRPCEN:', selectedCrpcen || 'not provided (will use token value)');
    // console.log('[SSO] Selected TenantId:', selectedTenantId || 'not provided (will use token value)');
    // console.log('═══════════════════════════════════════════════════════════════');
    
    // console.log('[SSO] 📤 Making POST request to:', SSO_EXCHANGE_TOKEN_ENDPOINT);
    
    const requestBody: any = {
      access_token: genapiToken,
    };
    
    // Add selected office info if provided (for multi-office users)
    if (selectedCrpcen) {
      requestBody.selected_crpcen = selectedCrpcen;
    }
    if (selectedTenantId) {
      requestBody.selected_tenant_id = selectedTenantId;
    }
    
    const response = await apiClient.post(SSO_EXCHANGE_TOKEN_ENDPOINT, requestBody);
    
    const authResult = response.data.AuthenticationResult;
    
    const tokens: Tokens = {
      IdToken: authResult.IdToken,
      AccessToken: authResult.AccessToken,
      RefreshToken: authResult.RefreshToken,
      ExpiresIn: authResult.ExpiresIn,
      TokenType: authResult.TokenType || 'Bearer',
      AuthMethod: 'sso',
      ExpiresOn: new Date(Date.now() + authResult.ExpiresIn * 1000).toISOString(),
    };
    
    // console.log('[SSO] ✅ Step 3 SUCCESS: Received Cognito tokens');
    // console.log('[SSO] IdToken (first 50 chars):', authResult.IdToken?.substring(0, 50) + '...');
    // console.log('═══════════════════════════════════════════════════════════════');
    return tokens;
    
  } catch (error) {
    console.error('═══════════════════════════════════════════════════════════════');
    console.error('[SSO] ❌❌❌ ERROR in exchangeSSOToken()');
    console.error('[SSO] Error object:', error);
    if (axios.isAxiosError(error)) {
      console.error('[SSO] Axios error details:');
      console.error('  - URL:', error.config?.url);
      console.error('  - Method:', error.config?.method);
      console.error('  - Status:', error.response?.status);
      console.error('  - Response data:', error.response?.data);
      console.error('  - Request data:', error.config?.data);
    }
    console.error('═══════════════════════════════════════════════════════════════');
    throw new Error('Failed to exchange SSO token for Cognito tokens');
  }
}

/**
 * Get user's offices (études) from GenAPI
 */
export async function getStudies(genapiToken: string): Promise<Office[]> {
  try {
    // console.log('═══════════════════════════════════════════════════════════════');
    // console.log('[SSO] 🟡 STEP 2: getStudies() called');
    // console.log('[SSO] Function: getStudies');
    // console.log('[SSO] URL:', SSO_STUDIES_URL);
    // console.log('[SSO] GenAPI token (first 50 chars):', genapiToken.substring(0, 50) + '...');
    // console.log('═══════════════════════════════════════════════════════════════');
    
    const response = await axios.get(SSO_STUDIES_URL!, {
      headers: {
        Authorization: `Bearer ${genapiToken}`,
      },
    });
    
    // console.log('[SSO] ✅ Step 2 SUCCESS: Fetched studies from GenAPI');
    // console.log('[SSO] Number of offices:', response.data.length);
    // console.log('[SSO] Offices:', response.data);
    // console.log('═══════════════════════════════════════════════════════════════');
    return response.data;
    
  } catch (error) {
    console.error('[SSO] Error fetching studies:', error);
    throw new Error('Failed to fetch user studies');
  }
}

/**
 * Decode GenAPI JWT token (for debugging/validation)
 */
export function decodeGenapiToken(token: string): any {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }
    
    const payload = JSON.parse(atob(parts[1]));
    return payload;
  } catch (error) {
    console.error('[SSO] Error decoding token:', error);
    return null;
  }
}

// ================================================================================
// Cross-Context SSO Functions (for Desktop Outlook)
// ================================================================================

/**
 * Store tokens in backend for cross-context retrieval
 * Used when SSO completes in browser but add-in needs the tokens
 * 
 * @param sessionId - The unique session identifier (from OAuth state)
 * @param tokens - Cognito tokens to store
 */
export async function storeTokensForPolling(sessionId: string, tokens: Tokens): Promise<void> {
  try {
    // console.log('[SSO] Storing tokens in backend for session:', sessionId.substring(0, 10) + '...');
    // console.log('[SSO] Token data:', {
    //   hasIdToken: !!tokens.IdToken,
    //   hasAccessToken: !!tokens.AccessToken,
    //   hasRefreshToken: !!tokens.RefreshToken,
    //   expiresIn: tokens.ExpiresIn
    // });
    
    await apiClient.post('/auth/sso/store-tokens', {
      session_id: sessionId,
      tokens: tokens,
    });
    
    // console.log(`[SSO] Tokens stored successfully`);
  } catch (error) {
    console.error('[SSO] Error storing tokens:', {
      error,
      sessionId: sessionId.substring(0, 10) + '...',
      message: error instanceof Error ? error.message : 'Unknown error'
    });
    throw new Error('Failed to store tokens for polling');
  }
}

/**
 * Poll backend for tokens by session ID
 * Returns tokens if available, null if not yet ready
 * 
 * @param sessionId - The unique session identifier
 * @returns Tokens if found, null if not yet available
 */
export async function pollForTokens(sessionId: string): Promise<Tokens | null> {
  try {
    const response = await apiClient.get(`/auth/sso/poll-tokens/${sessionId}`);
    
    if (response.data.found) {
      console.log('[SSO] Tokens retrieved from backend for session:', sessionId.substring(0, 10) + '...');
      return response.data.tokens;
    }
    
    // Not yet available
    return null;
  } catch (error) {
    console.error('[SSO] Error polling for tokens:', {
      sessionId: sessionId.substring(0, 10) + '...',
      error,
      message: error instanceof Error ? error.message : 'Unknown error'
    });
    return null;
  }
}

/**
 * Wait for tokens with polling (for cross-context SSO)
 * Polls backend until tokens are available or timeout
 * 
 * @param sessionId - The unique session identifier
 * @param maxAttempts - Maximum number of polling attempts (default: 60 = 5 minutes)
 * @param interval - Interval between attempts in milliseconds (default: 5000 = 5 seconds)
 * @param abortSignal - Optional AbortSignal to cancel the polling loop
 * @returns Cognito tokens when available
 * @throws Error if timeout, polling fails, or operation is aborted
 */
export async function waitForTokens(
  sessionId: string,
  maxAttempts: number = 60,
  interval: number = 5000,
  abortSignal?: AbortSignal
): Promise<Tokens> {
  const startTime = Date.now();
  console.log(`[SSO] Starting polling for session ${sessionId.substring(0, 10)}... (max ${maxAttempts} attempts, ${interval}ms interval)`);
  
  // Check if already aborted
  if (abortSignal?.aborted) {
    console.log('[SSO] Polling aborted before start');
    throw new Error('Polling operation was cancelled');
  }
  
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    // Check for abort signal
    if (abortSignal?.aborted) {
      const totalDuration = Math.round((Date.now() - startTime) / 1000);
      console.log(`[SSO] ✗ Polling cancelled after ${attempt} attempts (${totalDuration}s elapsed)`);
      throw new Error('Polling operation was cancelled');
    }
    
    const elapsed = Math.round((Date.now() - startTime) / 1000);
    console.log(`[SSO] Polling attempt ${attempt + 1}/${maxAttempts} (${elapsed}s elapsed)`);
    
    const tokens = await pollForTokens(sessionId);
    
    if (tokens) {
      const totalDuration = Math.round((Date.now() - startTime) / 1000);
      console.log(`[SSO] ✓ Tokens received after ${attempt + 1} attempts (${totalDuration}s total)`);
      return tokens;
    }
    
    // Wait before next attempt (unless it's the last attempt)
    if (attempt < maxAttempts - 1) {
      // Use abortable sleep
      await new Promise<void>((resolve, reject) => {
        const timeoutId = setTimeout(resolve, interval);
        
        // Listen for abort signal during sleep
        if (abortSignal) {
          const abortHandler = () => {
            clearTimeout(timeoutId);
            reject(new Error('Polling operation was cancelled'));
          };
          
          if (abortSignal.aborted) {
            clearTimeout(timeoutId);
            reject(new Error('Polling operation was cancelled'));
          } else {
            abortSignal.addEventListener('abort', abortHandler, { once: true });
          }
        }
      });
    }
  }
  
  const totalDuration = Math.round((Date.now() - startTime) / 1000);
  console.error(`[SSO] ✗ Timeout after ${maxAttempts} attempts (${totalDuration}s total)`);
  throw new Error('Timeout waiting for SSO tokens');
}
